import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ExecutionError, Rpc } from 'accounts'
import { useMemo, useState } from 'react'
import { isAddress, parseUnits } from 'viem'
import { useConnection } from 'wagmi'
import { Hooks } from 'wagmi/tempo'
import * as z from 'zod/mini'

import { useAssets } from '#/lib/assets.js'
import { getSupportedChainId } from '#/lib/chain.js'
import { formatQuantity, isTip20 } from '#/lib/token.js'
import { chain as zoneChain, zoneId as defaultZoneId } from '#/lib/zone.js'
import { RequireZoneAuth } from '../-components/RequireZoneAuth.js'
import * as ZoneFrames from '../-frames/zones/index.js'
import * as RemoteRpc from '../-lib/RemoteRpc.js'

const decimalRegex = /^\d*\.?\d*$/

export const Route = createFileRoute('/remote/rpc/wallet_withdrawZone')({
  component: Component,
  validateSearch: (search) => RemoteRpc.validateSearch(search, { method: 'wallet_withdrawZone' }),
})

function Component() {
  const request = Route.useSearch()
  const rpc = RemoteRpc.use()

  const host = RemoteRpc.useExternalHost()

  // Iframe Storage doesn't share with the parent dashboard, so the user
  // must (re-)sign a zone authorization token before any private-zone
  // read or write can happen.
  return (
    <RequireZoneAuth host={host} onReject={() => rpc.reject()}>
      <Withdraw host={host} request={request} />
    </RequireZoneAuth>
  )
}

function Withdraw(props: {
  host: string | undefined
  request: ReturnType<typeof Route.useSearch>
}) {
  const { host, request } = props
  const rpc = RemoteRpc.use()
  const { address, chainId } = useConnection()

  const params = request._decoded?.params?.[0]
  const parentChainId = getSupportedChainId(chainId)
  const requestedZoneId = params?.zoneId ?? defaultZoneId

  // wallet-next currently supports a single zone; reject other zone ids.
  const unsupportedZone = requestedZoneId !== defaultZoneId

  const [values, setValues] = useState(() => ({
    amount: params?.amount ?? '',
    token: params?.token ?? '',
  }))

  // `zones: true` makes the worker skip the parent fetch and return one row
  // per (token, zone) holding with `zoneId` set on the row itself.
  const assets = useAssets({ account: address, chainId: parentChainId, zones: true })

  const sendableAssets = useMemo(() => {
    if (!assets.data) return undefined
    return assets.data
      .filter((a) => a.zoneId === requestedZoneId && isTip20(a.address) && BigInt(a.balance) > 0n)
      .slice()
      .sort((a, b) => {
        const aBal = BigInt(a.balance)
        const bBal = BigInt(b.balance)
        if (aBal === bBal) return a.symbol.localeCompare(b.symbol)
        return bBal > aBal ? 1 : -1
      })
      .map((a) => ({
        address: a.address,
        balance: a.balance,
        decimals: a.decimals,
        symbol: a.symbol,
      }))
  }, [assets.data, requestedZoneId])

  const effectiveToken = useMemo(() => {
    if (values.token) return values.token
    if (!sendableAssets || sendableAssets.length === 0) return ''
    const preferred =
      params?.token &&
      sendableAssets.find((a) => a.address.toLowerCase() === params.token!.toLowerCase())
    return (preferred ?? sendableAssets[0]).address
  }, [params?.token, sendableAssets, values.token])

  const selectedAsset = useMemo(() => {
    if (!effectiveToken || !sendableAssets) return undefined
    return sendableAssets.find((a) => a.address.toLowerCase() === effectiveToken.toLowerCase())
  }, [effectiveToken, sendableAssets])

  const tokenError = useMemo(() => {
    if (unsupportedZone) return `Zone ${requestedZoneId} is not supported.`
    if (!effectiveToken) return undefined
    if (!isAddress(effectiveToken)) return 'Invalid asset.'
    if (!isTip20(effectiveToken)) return 'Only TIP-20 assets can be withdrawn from a zone.'
    if (!selectedAsset && !assets.isLoading)
      return 'You don’t have a balance for this asset in the zone.'
    return undefined
  }, [assets.isLoading, effectiveToken, requestedZoneId, selectedAsset, unsupportedZone])

  const amount = useMemo<bigint | undefined>(() => {
    if (!values.amount || !selectedAsset || values.amount === '.') return undefined
    const normalized = values.amount.startsWith('.')
      ? `0${values.amount}`
      : values.amount.endsWith('.')
        ? values.amount.slice(0, -1)
        : values.amount
    const decimals = normalized.split('.')[1]?.length ?? 0
    if (decimals > selectedAsset.decimals) return undefined
    try {
      return parseUnits(normalized, selectedAsset.decimals)
    } catch {
      return undefined
    }
  }, [selectedAsset, values.amount])

  const amountError = useMemo(() => {
    if (!values.amount) return undefined
    if (!decimalRegex.test(values.amount) || values.amount === '.') return 'Enter a valid amount.'
    if (!selectedAsset) return undefined
    const decimals = values.amount.split('.')[1]?.length ?? 0
    if (decimals > selectedAsset.decimals) return `Use at most ${selectedAsset.decimals} decimals.`
    if (amount === undefined) return 'Enter a valid amount.'
    if (amount <= 0n) return 'Enter an amount greater than 0.'
    if (amount > BigInt(selectedAsset.balance)) return 'Insufficient zone balance.'
    return undefined
  }, [amount, selectedAsset, values.amount])

  const withdraw = Hooks.zone.useRequestWithdrawalSync()

  const confirm = useMutation({
    mutationFn: async () => {
      if (!selectedAsset) throw new Error('Select an asset.')
      if (amount === undefined || amount <= 0n) throw new Error('Enter an amount.')
      const { receipt } = await withdraw.mutateAsync({
        amount,
        chainId: zoneChain.id,
        feeToken: selectedAsset.address,
        token: selectedAsset.address,
      })
      await rpc.resolve({ receipt: z.encode(Rpc.receipt as never, receipt as never) })
      return receipt
    },
  })

  const fieldErrors = !!(amountError || tokenError)
  const alert = useMemo(() => {
    if (fieldErrors) return null
    if (confirm.error)
      return { type: 'error' as const, message: ExecutionError.parse(confirm.error).message }
    return null
  }, [confirm.error, fieldErrors])

  const ready =
    !!selectedAsset && amount !== undefined && amount > 0n && !fieldErrors && !confirm.isPending

  const primaryLabel = confirm.isError
    ? 'Try again'
    : confirm.isPending
      ? 'Withdrawing…'
      : 'Withdraw'

  // Asset rows for the dropdown.
  const assetOptions = useMemo(() => {
    if (!sendableAssets) return undefined
    return sendableAssets.map((a) => ({
      address: a.address,
      balance: formatQuantity(BigInt(a.balance), a.decimals),
      symbol: a.symbol,
    }))
  }, [sendableAssets])

  return (
    <ZoneFrames.Withdraw
      alert={alert}
      assets={assetOptions}
      assetsLoading={assets.isLoading}
      chainId={parentChainId}
      confirming={confirm.isPending}
      errors={{ amount: amountError, token: tokenError }}
      host={host}
      loading={!sendableAssets}
      onChange={(event) => {
        confirm.reset()
        if (event.field === 'amount') {
          const next = event.value.replaceAll(',', '.')
          if (!decimalRegex.test(next)) return
          setValues((current) => ({ ...current, amount: next }))
          return
        }
        setValues((current) => ({ ...current, token: event.value }))
      }}
      onConfirm={() => confirm.mutate()}
      onMax={
        selectedAsset
          ? () => {
              confirm.reset()
              setValues((current) => ({
                ...current,
                amount: formatQuantity(
                  BigInt(selectedAsset.balance),
                  selectedAsset.decimals,
                ).replaceAll(',', ''),
              }))
            }
          : undefined
      }
      onReject={() => rpc.reject()}
      primaryLabel={primaryLabel}
      ready={ready}
      selectedAsset={
        selectedAsset
          ? {
              address: selectedAsset.address,
              balance: formatQuantity(BigInt(selectedAsset.balance), selectedAsset.decimals),
              symbol: selectedAsset.symbol,
            }
          : undefined
      }
      values={{ amount: values.amount }}
    />
  )
}
